import { useState, useEffect, useCallback, lazy, Suspense, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient, ApiError } from '../lib/apiClient';
import SourceBadge from './SourceBadge';
import { useGeolocation } from '../hooks/useGeolocation';
import { ContractForm, InventoryForm, InsuranceForm, FieldForm, MachineForm, HerdForm, EnergyForm } from './EntityForms';
import { AnimatedPage } from './motion';

const FarmMap = lazy(() => import('./FarmMap'));

/**
 * Farm Overview Page — Betriebsspiegel / Farm Cockpit.
 * Shows everything the system knows about the farm in a structured cockpit view.
 */
export default function FarmOverviewPage({ onClose, token, user, onUserUpdate }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [fruchtfolge, setFruchtfolge] = useState(null);
  const [contracts, setContracts] = useState(null);
  const [inventoryValue, setInventoryValue] = useState(null);
  const [cashflow, setCashflow] = useState(null);
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({ stammdaten: true, kulturen: true });
  const [activeForm, setActiveForm] = useState(null); // {type: 'contract'|'inventory'|..., initial: null|obj}
  const [inventory, setInventory] = useState(null);
  const [insurance, setInsurance] = useState(null);
  const [herds, setHerds] = useState(null);
  const [energy, setEnergy] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null); // { msg, type: 'ok'|'error'|'loading' }
  const [importStatus, setImportStatus] = useState(null); // {msg, type: 'ok'|'error'|'loading'}
  const { position, requestLocation, loading: gpsLoading } = useGeolocation();

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchOverview = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        fetch('/api/v1/aif/auth/farm/overview', { headers }),
        fetch('/api/v1/aif/farm/fruchtfolge', { headers }),
        fetch('/api/v1/aif/farm/contracts', { headers }),
        fetch('/api/v1/aif/farm/inventory/value', { headers }),
        fetch('/api/v1/aif/finance/cashflow?months=12', { headers }),
        fetch('/api/v1/aif/finance/contribution-margin', { headers }),
        fetch('/api/v1/aif/farm/inventory', { headers }),
        fetch('/api/v1/aif/farm/insurance', { headers }),
        fetch('/api/v1/aif/farm/herds', { headers }),
        fetch('/api/v1/aif/farm/energy', { headers }),
      ]);
      if (results[0].status === 'fulfilled' && results[0].value.ok) setData(await results[0].value.json());
      if (results[1].status === 'fulfilled' && results[1].value.ok) setFruchtfolge(await results[1].value.json());
      if (results[2].status === 'fulfilled' && results[2].value.ok) setContracts(await results[2].value.json());
      if (results[3].status === 'fulfilled' && results[3].value.ok) setInventoryValue(await results[3].value.json());
      if (results[4].status === 'fulfilled' && results[4].value.ok) setCashflow(await results[4].value.json());
      if (results[5].status === 'fulfilled' && results[5].value.ok) setFinance(await results[5].value.json());
      if (results[6].status === 'fulfilled' && results[6].value.ok) setInventory(await results[6].value.json());
      if (results[7].status === 'fulfilled' && results[7].value.ok) setInsurance(await results[7].value.json());
      if (results[8].status === 'fulfilled' && results[8].value.ok) setHerds(await results[8].value.json());
      if (results[9].status === 'fulfilled' && results[9].value.ok) setEnergy(await results[9].value.json());
    } catch { /* network errors handled by individual promise results */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  const handleFlaechenantragImport = async (file) => {
    if (!file || !token) return;
    setImportStatus({ msg: `${file.name} ${t('import.importLoading')}`, type: 'loading' });
    try {
      const formData = new FormData();
      formData.append('file', file);
      // TODO(a6-sub6-formdata): migrate when apiClient gains FormData/multipart support
      /* eslint-disable no-restricted-syntax -- FormData upload; apiClient requires JSON */
      const res = await fetch('/api/v1/aif/import/flaechenantrag', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      /* eslint-enable no-restricted-syntax */
      const data = res.ok ? await res.json() : null;
      if (data && data.imported > 0) {
        setImportStatus({ msg: `✓ ${data.summary}`, type: 'ok' });
        setTimeout(() => { setImportStatus(null); fetchOverview(); }, 4000);
      } else if (data && data.fallback_message) {
        setImportStatus({ msg: data.fallback_message, type: 'error' });
        setTimeout(() => setImportStatus(null), 8000);
      } else {
        setImportStatus({ msg: t('import.importFailed'), type: 'error' });
        setTimeout(() => setImportStatus(null), 4000);
      }
    } catch {
      setImportStatus({ msg: t('app.errorNetwork'), type: 'error' });
      setTimeout(() => setImportStatus(null), 3000);
    }
  };

  const handleCsvImport = async (file, entityType) => {
    if (!file || !token) return;
    setImportStatus({ msg: `${file.name} ${t('import.importLoading')}`, type: 'loading' });
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('entity_type', entityType);
      // TODO(a6-sub6-formdata): migrate when apiClient gains FormData/multipart support
      /* eslint-disable no-restricted-syntax -- FormData upload; apiClient requires JSON */
      const res = await fetch('/api/v1/aif/import/csv', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      /* eslint-enable no-restricted-syntax */
      const data = res.ok ? await res.json() : null;
      if (data) {
        const errCount = data.errors?.length || 0;
        const msg = `✓ ${data.summary}${errCount > 0 ? ` (${errCount} ${t('import.errors')})` : ''}`;
        setImportStatus({ msg, type: data.imported > 0 ? 'ok' : 'error' });
        if (data.imported > 0) setTimeout(() => { setImportStatus(null); fetchOverview(); }, 5000);
        else setTimeout(() => setImportStatus(null), 8000);
      } else {
        setImportStatus({ msg: t('import.importFailed'), type: 'error' });
        setTimeout(() => setImportStatus(null), 4000);
      }
    } catch {
      setImportStatus({ msg: t('app.errorNetwork'), type: 'error' });
      setTimeout(() => setImportStatus(null), 3000);
    }
  };

  const handleDocumentUpload = async (file, context) => {
    if (!file || !token) return;
    setUploadStatus({ msg: `${file.name} ${t('app.uploadAnalyzing')}`, type: 'loading' });
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (context) formData.append('message', context);
      // TODO(a6-sub6-formdata): migrate when apiClient gains FormData/multipart support
      /* eslint-disable no-restricted-syntax -- FormData upload; apiClient requires JSON */
      const res = await fetch('/api/v1/aif/chat/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      /* eslint-enable no-restricted-syntax */
      if (res.ok) {
        const data = await res.json();
        setUploadStatus({ msg: `✓ ${file.name} ${t('app.uploadDone')}`, type: 'ok' });
        setTimeout(() => setUploadStatus(null), 4000);
      } else {
        setUploadStatus({ msg: t('app.uploadError'), type: 'error' });
        setTimeout(() => setUploadStatus(null), 3000);
      }
    } catch {
      setUploadStatus({ msg: t('app.errorNetwork'), type: 'error' });
      setTimeout(() => setUploadStatus(null), 3000);
    }
  };

  const saveFarm = async (updates) => {
    setSaving(true);
    try {
      const r = await apiClient.patch('/api/v1/aif/auth/farm', updates, {
        entityType: 'farm', queueOnOffline: true,
      });
      if (r?.data) onUserUpdate?.(r.data);
      await fetchOverview();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
    setSaving(false);
  };

  const saveFieldCoords = async (fieldId, lat, lng) => {
    try {
      // TODO(5b): activate concurrency — field is 3WM
      await apiClient.patch(`/api/v1/aif/farm/fields/${fieldId}`, { latitude: lat, longitude: lng }, {
        entityType: 'field', queueOnOffline: true,
      });
      await fetchOverview();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };

  const updateGPS = async () => {
    requestLocation();
    if (position) {
      await saveFarm({ latitude: position.latitude, longitude: position.longitude });
    }
  };

  useEffect(() => {
    if (position && data?.sections?.stammdaten?.latitude == null) {
      saveFarm({ latitude: position.latitude, longitude: position.longitude });
    }
  }, [position]);

  const toggle = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // CRUD helpers
  const apiSave = async (url, method, data) => {
    try {
      // TODO(5b): activate concurrency — callers include field/inventory/herd (3WM)
      const fn = method === 'POST' ? apiClient.post : apiClient.patch;
      await fn(url, data, { entityType: 'farm_entity', queueOnOffline: true });
      setActiveForm(null);
      await fetchOverview();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };
  const apiDelete = async (url) => {
    try {
      // TODO(5b): activate concurrency — callers include field/inventory/herd (3WM)
      await apiClient.delete(url, { entityType: 'farm_entity', queueOnOffline: true });
      setActiveForm(null);
      await fetchOverview();
    } catch (err) {
      if (!(err instanceof ApiError)) throw err;
    }
  };

  const s = data?.sections || {};
  const stamm = s.stammdaten || {};
  const comp = data?.completeness || {};
  const fields = s.felder?.fields || [];
  const machines = s.maschinen?.machinery || [];
  const crops = s.kulturen?.crops || [];
  const livestock = s.tierhaltung?.livestock_types || [];

  // Betriebsspiegel metrics
  const totalHa = stamm.total_ha || 0;
  const fieldCount = fields.length;
  const cropCount = crops.length;
  const machineCount = machines.length;
  const contractCount = contracts?.count || 0;
  const invValue = inventoryValue?.total_value || 0;
  const invCurrency = inventoryValue?.currency || 'EUR';
  const avgDbHa = finance?.avg_db_i_per_ha || 0;
  const finCurrency = finance?.currency || 'EUR';
  const hasGap = cashflow?.has_gaps || false;

  // Enterprise branches
  const branches = [
    { key: 'crop', label: t('map.branchCrop'), active: cropCount > 0 || fieldCount > 0 },
    { key: 'livestock', label: t('map.branchLivestock'), active: livestock.length > 0 },
    { key: 'energy', label: t('map.branchEnergy'), active: false },
    { key: 'direct', label: t('map.branchDirect'), active: false },
  ];

  // Context-aware next steps
  const getNextStep = () => {
    if (!stamm.region_code) return t('map.stepRegion');
    if (!stamm.total_ha) return t('map.stepHa');
    if (cropCount === 0) return t('map.stepCrops');
    if (fieldCount === 0) return t('map.stepFields');
    if (!stamm.latitude) return t('map.stepGps');
    if (contractCount === 0) return t('map.stepContracts');
    return t('map.stepComplete');
  };

  return (
    <AnimatedPage className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('farm.title')}</span>
      </header>

      {/* Upload status toast */}
      {uploadStatus && (
        <div className={`mx-4 mt-2 px-3 py-2 rounded-xl text-xs font-medium flex items-center gap-2 ${
          uploadStatus.type === 'ok' ? 'bg-green-500/10 border border-green-500/30 text-green-400' :
          uploadStatus.type === 'error' ? 'bg-red-500/10 border border-red-500/30 text-red-400' :
          'bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-[var(--aif-text-muted)]'
        }`}>
          {uploadStatus.type === 'loading' && <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin flex-shrink-0" />}
          {uploadStatus.msg}
        </div>
      )}

      {/* Import status toast */}
      {importStatus && (
        <div className={`mx-4 mt-2 px-3 py-2 rounded-xl text-xs font-medium flex items-center gap-2 ${
          importStatus.type === 'ok' ? 'bg-green-500/10 border border-green-500/30 text-green-400' :
          importStatus.type === 'error' ? 'bg-amber-500/10 border border-amber-500/30 text-amber-400' :
          'bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] text-[var(--aif-text-muted)]'
        }`}>
          {importStatus.type === 'loading' && <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin flex-shrink-0" />}
          <span className="leading-relaxed">{importStatus.msg}</span>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 py-4 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-8 h-8 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {/* Completeness bar */}
              <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-[var(--aif-text-primary)]">{t('farm.completeness')}</span>
                  <span className="text-sm font-bold text-[var(--aif-color-primary)]">{comp.score_pct || 0}%</span>
                </div>
                <div className="w-full h-2 bg-[var(--aif-bg-elevated)] rounded-full overflow-hidden">
                  <div className="h-full bg-[var(--aif-color-primary)] rounded-full transition-all" style={{ width: `${comp.score_pct || 0}%` }} />
                </div>
                <p className="text-xs text-[var(--aif-text-muted)] mt-2">{getNextStep()}</p>
              </div>

              {/* Farm Map */}
              {stamm.latitude && (
                <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] overflow-hidden">
                  <div className="px-4 py-2 flex items-center justify-between">
                    <span className="text-sm font-medium text-[var(--aif-text-primary)]">{t('map.title')}</span>
                    <span className="text-[10px] text-[var(--aif-text-muted)]">{stamm.latitude?.toFixed(4)}, {stamm.longitude?.toFixed(4)}</span>
                  </div>
                  <Suspense fallback={<div className="h-[250px] bg-[var(--aif-bg-elevated)] animate-pulse" />}>
                    <FarmMap
                      farmLat={stamm.latitude}
                      farmLng={stamm.longitude}
                      farmName={stamm.name || t('farm.title')}
                      fields={fields}
                      onFieldCoordsUpdate={saveFieldCoords}
                    />
                  </Suspense>
                </div>
              )}
              {!stamm.latitude && (
                <button
                  onClick={updateGPS}
                  disabled={gpsLoading}
                  className="w-full rounded-2xl border border-dashed border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-6 text-center"
                >
                  <div className="text-2xl mb-2">📍</div>
                  <div className="text-sm font-medium text-[var(--aif-text-primary)]">
                    {gpsLoading ? t('wizard.gpsLoading') : t('map.addLocation')}
                  </div>
                  <div className="text-xs text-[var(--aif-text-muted)] mt-1">{t('map.addLocationHint')}</div>
                </button>
              )}

              {/* Betriebsspiegel — key figures grid */}
              <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
                <h3 className="text-sm font-medium text-[var(--aif-text-primary)] mb-3">{t('map.cockpit')}</h3>
                <div className="grid grid-cols-4 gap-2">
                  <MetricCell value={`${totalHa}`} unit="ha" label={t('map.area')} />
                  <MetricCell value={`${cropCount}`} label={t('dashboard.crops')} />
                  <MetricCell value={`${fieldCount}`} label={t('dashboard.fieldCount')} />
                  <MetricCell value={`${machineCount}`} label={t('farm.machinery')} />
                  <MetricCell value={`${contractCount}`} label={t('contracts.title')} />
                  <MetricCell value={invValue > 0 ? `${(invValue / 1000).toFixed(0)}k` : '—'} unit={invCurrency} label={t('inventory.title')} />
                  <MetricCell value={avgDbHa > 0 ? `${avgDbHa.toLocaleString()}` : '—'} unit={`${finCurrency}/ha`} label={t('finance.dbI')} />
                  {cashflow && <MetricCell value={hasGap ? '!' : t('farm.statusOk')} label={t('cashflow.title')} warning={hasGap} />}
                </div>
              </div>

              {/* Enterprise Branches — only show active ones */}
              {branches.some(b => b.active) && (
                <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] p-4">
                  <h3 className="text-sm font-medium text-[var(--aif-text-primary)] mb-3">{t('map.branches')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {branches.filter(b => b.active).map(b => (
                      <span key={b.key} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-green-500/40 bg-green-500/10 text-green-400">
                        {b.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Stammdaten */}
              <Section title={t('farm.stammdaten')} expanded={expanded.stammdaten} onToggle={() => toggle('stammdaten')}>
                <DataField label={t('farm.name')} value={stamm.name} />
                <DataField label={t('farm.region')} value={stamm.region_code} />
                <DataField label={t('farm.country')} value={stamm.country_code} />
                <DataField label={t('farm.totalHa')} value={stamm.total_ha ? `${stamm.total_ha} ha` : null} />
                <DataField label={t('farm.organic')} value={stamm.organic_certified != null ? (stamm.organic_certified ? t('wizard.organic') : t('wizard.conventional')) : null} />
                <DataField label={t('farm.postalCode')} value={stamm.postal_code} />
                <DataField label={t('farm.gps')} value={stamm.latitude ? `${stamm.latitude.toFixed(4)}, ${stamm.longitude.toFixed(4)}` : null} />
                {!stamm.latitude && (
                  <button onClick={updateGPS} disabled={gpsLoading} className="text-xs text-[var(--aif-color-primary)] mt-1">
                    {gpsLoading ? t('wizard.gpsLoading') : t('farm.updateLocation')}
                  </button>
                )}
              </Section>

              {/* Kulturen */}
              <Section title={`${t('farm.crops')} (${cropCount})`} expanded={expanded.kulturen} onToggle={() => toggle('kulturen')}>
                <div className="flex flex-wrap gap-1.5">
                  {crops.map((c, i) => (
                    <Tag key={i} label={typeof c === 'object' ? c.name : c} source="ai" />
                  ))}
                  {!cropCount && <span className="text-xs text-[var(--aif-text-muted)]">{t('farm.noCrops')}</span>}
                </div>
              </Section>

              {/* Felder */}
              <Section title={`${t('farm.fields')} (${fieldCount})${s.felder?.total_ha ? ` — ${s.felder.total_ha} ha` : ''}`} expanded={expanded.felder} onToggle={() => toggle('felder')}>
                {fieldCount > 0 ? (
                  <div className="space-y-1.5">
                    {fields.map((f, i) => (
                      <button key={f.id || i} onClick={() => setActiveForm({ type: 'field', initial: f })} className="w-full flex items-center justify-between py-1.5 text-sm text-left hover:bg-[var(--aif-bg-elevated)] rounded px-1 transition">
                        <div className="flex items-center gap-2">
                          <span className="text-[var(--aif-text-primary)] font-medium">{f.name || `${t('farm.fieldName')} ${i + 1}`}</span>
                          {f.latitude && <span className="text-[10px] text-green-400">📍</span>}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-[var(--aif-text-muted)]">
                          {f.area_ha && <span>{f.area_ha} ha</span>}
                          {f.current_crop_name && <Tag label={f.current_crop_name} source={f.source || 'db'} />}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-[var(--aif-text-muted)]">{t('farm.noFields')}</span>
                )}
                <AddButton label={t('form.addField')} onClick={() => setActiveForm({ type: 'field' })} />
                <div className="flex gap-2 mt-1">
                  <ImportButton
                    label={t('import.flaechenantrag')}
                    accept=".pdf"
                    onFile={handleFlaechenantragImport}
                  />
                  <ImportButton
                    label={t('import.csvFields')}
                    accept=".csv,.txt"
                    onFile={(f) => handleCsvImport(f, 'fields')}
                  />
                </div>
              </Section>

              {/* Tierhaltung */}
              <Section title={`${t('farm.livestock')} (${herds?.count || livestock.length})`} expanded={expanded.tierhaltung} onToggle={() => toggle('tierhaltung')}>
                {(herds?.herds || []).length > 0 ? (
                  <div className="space-y-1.5">
                    {herds.herds.map((h, i) => (
                      <button key={i} onClick={() => setActiveForm({ type: 'herd', initial: h })} className="w-full flex items-center justify-between py-1.5 text-sm text-left hover:bg-[var(--aif-bg-elevated)] rounded px-1 transition">
                        <span className="text-[var(--aif-text-primary)]">{h.species_name}{h.breed ? ` (${h.breed})` : ''}</span>
                        <span className="text-xs text-[var(--aif-text-muted)]">{h.head_count} {t('dashboard.heads')}</span>
                      </button>
                    ))}
                  </div>
                ) : livestock.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {livestock.map((l, i) => <Tag key={i} label={l} source="ai" />)}
                  </div>
                ) : (
                  <span className="text-xs text-[var(--aif-text-muted)]">{t('farm.noLivestock')}</span>
                )}
                <AddButton label={t('form.addHerd')} onClick={() => setActiveForm({ type: 'herd' })} />
              </Section>

              {/* Maschinen */}
              <Section title={`${t('farm.machinery')} (${machineCount})`} expanded={expanded.maschinen} onToggle={() => toggle('maschinen')}>
                {machineCount > 0 ? (
                  <div className="space-y-1.5">
                    {machines.map((m, i) => (
                      <button key={m.id || i} onClick={() => typeof m === 'object' && m.id ? setActiveForm({ type: 'machine', initial: m }) : null} className="w-full flex items-center justify-between py-1.5 text-sm text-left hover:bg-[var(--aif-bg-elevated)] rounded px-1 transition">
                        <span className="text-[var(--aif-text-primary)]">
                          {typeof m === 'object' ? (m.make && m.model ? `${m.make} ${m.model}` : m.name) : m}
                        </span>
                        <div className="flex items-center gap-2 text-xs text-[var(--aif-text-muted)]">
                          {typeof m === 'object' && m.engine_hp && <span>{m.engine_hp} {t('farm.hpUnit')}</span>}
                          {typeof m === 'object' && m.machine_type && <span>{m.machine_type}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-[var(--aif-text-muted)]">{t('farm.noMachinery')}</span>
                )}
                <AddButton label={t('form.addMachine')} onClick={() => setActiveForm({ type: 'machine' })} />
              </Section>

              {/* Fruchtfolge */}
              {fruchtfolge?.fields?.length > 0 && (
                <Section title={t('farm.fruchtfolge')} expanded={expanded.fruchtfolge} onToggle={() => toggle('fruchtfolge')}>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          <th className="text-left py-1 pr-3 text-[var(--aif-text-muted)] font-medium">{t('farm.fieldName')}</th>
                          {(fruchtfolge.years || []).map(y => (
                            <th key={y} className="text-center py-1 px-2 text-[var(--aif-text-muted)] font-medium">{y}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {fruchtfolge.fields.map((f, i) => (
                          <tr key={i} className="border-t border-[var(--aif-border-default)]">
                            <td className="py-1.5 pr-3 text-[var(--aif-text-primary)] font-medium whitespace-nowrap">{f.field_name}</td>
                            {(fruchtfolge.years || []).map(y => {
                              const crop = f.years?.[String(y)];
                              return (
                                <td key={y} className="text-center py-1.5 px-2">
                                  {crop ? (
                                    <span className="inline-block px-1.5 py-0.5 rounded bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)]">{crop.crop_name}</span>
                                  ) : (
                                    <span className="text-[var(--aif-text-muted)]">—</span>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>
              )}

              {/* Vertraege */}
              <Section title={`${t('farm.contracts')} (${contractCount})`} expanded={expanded.vertraege} onToggle={() => toggle('vertraege')}>
                {contractCount > 0 ? (
                  <div className="space-y-1.5">
                    {(contracts?.contracts || []).slice(0, 10).map((c, i) => (
                      <button key={i} onClick={() => setActiveForm({ type: 'contract', initial: c })} className="w-full flex items-center justify-between py-1.5 text-sm text-left hover:bg-[var(--aif-bg-elevated)] rounded px-1 transition">
                        <span className="text-[var(--aif-text-primary)]">{c.counterparty_name || c.contract_type}</span>
                        <div className="flex items-center gap-2 text-xs text-[var(--aif-text-muted)]">
                          {c.payment_amount && <span>{c.payment_amount} {c.payment_currency}</span>}
                          <span className={c.status === 'EXPIRING_SOON' ? 'text-orange-400' : ''}>{t(`contracts.${c.contract_type}`)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-[var(--aif-text-muted)]">{t('contracts.noContracts')}</span>
                )}
                <AddButton label={t('form.addContract')} onClick={() => setActiveForm({ type: 'contract' })} />
                <UploadButton label={t('app.uploadContract')} context={t('farm.contractUploadContext')} onUpload={handleDocumentUpload} />
              </Section>

              {/* Lager */}
              <Section title={`${t('farm.inventory')}${invValue > 0 ? ` — ${invValue.toLocaleString()} ${invCurrency}` : ''}`} expanded={expanded.lager} onToggle={() => toggle('lager')}>
                {(inventory?.items || []).length > 0 ? (
                  <div className="space-y-1.5">
                    {inventory.items.slice(0, 10).map((item, i) => (
                      <button key={i} onClick={() => setActiveForm({ type: 'inventory', initial: item })} className="w-full flex items-center justify-between py-1.5 text-sm text-left hover:bg-[var(--aif-bg-elevated)] rounded px-1 transition">
                        <span className="text-[var(--aif-text-primary)]">{item.item_name}</span>
                        <span className="text-xs text-[var(--aif-text-muted)]">{item.quantity} {item.unit}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-[var(--aif-text-muted)]">{t('inventory.noItems')}</span>
                )}
                <AddButton label={t('form.addInventory')} onClick={() => setActiveForm({ type: 'inventory' })} />
              </Section>

              {/* Versicherungen */}
              <Section title={`${t('finance.insuranceTitle')} (${insurance?.count || 0})`} expanded={expanded.insurance} onToggle={() => toggle('insurance')}>
                {(insurance?.policies || []).length > 0 ? (
                  <div className="space-y-1.5">
                    {insurance.policies.map((p, i) => (
                      <button key={i} onClick={() => setActiveForm({ type: 'insurance', initial: p })} className="w-full flex items-center justify-between py-1.5 text-sm text-left hover:bg-[var(--aif-bg-elevated)] rounded px-1 transition">
                        <span className="text-[var(--aif-text-primary)]">{p.insurer_name || p.policy_type}</span>
                        <span className="text-xs text-[var(--aif-text-muted)]">{p.premium_annual} {t('farm.eurPerYear')}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-[var(--aif-text-muted)]">{t('finance.noInsurance')}</span>
                )}
                <AddButton label={t('form.addInsurance')} onClick={() => setActiveForm({ type: 'insurance' })} />
              </Section>
            </>
          )}
        </div>
      </div>

      {/* Form Modals */}
      {activeForm?.type === 'contract' && (
        <ContractForm
          initial={activeForm.initial}
          onCancel={() => setActiveForm(null)}
          onSave={(data) => activeForm.initial?.id
            ? apiSave(`/api/v1/aif/farm/contracts/${activeForm.initial.id}`, 'PATCH', data)
            : apiSave('/api/v1/aif/farm/contracts', 'POST', data)}
          onDelete={activeForm.initial?.id ? () => apiDelete(`/api/v1/aif/farm/contracts/${activeForm.initial.id}`) : undefined}
        />
      )}
      {activeForm?.type === 'inventory' && (
        <InventoryForm
          initial={activeForm.initial}
          onCancel={() => setActiveForm(null)}
          onSave={(data) => activeForm.initial?.id
            ? apiSave(`/api/v1/aif/farm/inventory/${activeForm.initial.id}`, 'PATCH', data)
            : apiSave('/api/v1/aif/farm/inventory', 'POST', data)}
          onDelete={activeForm.initial?.id ? () => apiDelete(`/api/v1/aif/farm/inventory/${activeForm.initial.id}`) : undefined}
        />
      )}
      {activeForm?.type === 'insurance' && (
        <InsuranceForm
          initial={activeForm.initial}
          onCancel={() => setActiveForm(null)}
          onSave={(data) => activeForm.initial?.id
            ? apiSave(`/api/v1/aif/farm/insurance/${activeForm.initial.id}`, 'PATCH', data)
            : apiSave('/api/v1/aif/farm/insurance', 'POST', data)}
          onDelete={activeForm.initial?.id ? () => apiDelete(`/api/v1/aif/farm/insurance/${activeForm.initial.id}`) : undefined}
        />
      )}
      {activeForm?.type === 'field' && (
        <FieldForm
          initial={activeForm.initial}
          onCancel={() => setActiveForm(null)}
          onSave={(data) => activeForm.initial?.id
            ? apiSave(`/api/v1/aif/farm/fields/${activeForm.initial.id}`, 'PATCH', data)
            : apiSave('/api/v1/aif/farm/fields', 'POST', data)}
          onDelete={activeForm.initial?.id ? () => apiDelete(`/api/v1/aif/farm/fields/${activeForm.initial.id}`) : undefined}
        />
      )}
      {activeForm?.type === 'machine' && (
        <MachineForm
          initial={activeForm.initial}
          onCancel={() => setActiveForm(null)}
          onSave={(data) => activeForm.initial?.id
            ? apiSave(`/api/v1/aif/farm/machines/${activeForm.initial.id}`, 'PATCH', data)
            : apiSave('/api/v1/aif/farm/machines', 'POST', data)}
          onDelete={activeForm.initial?.id ? () => apiDelete(`/api/v1/aif/farm/machines/${activeForm.initial.id}`) : undefined}
        />
      )}
      {activeForm?.type === 'herd' && (
        <HerdForm
          initial={activeForm.initial}
          onCancel={() => setActiveForm(null)}
          onSave={(data) => activeForm.initial?.id
            ? apiSave(`/api/v1/aif/farm/herds/${activeForm.initial.id}`, 'PATCH', data)
            : apiSave('/api/v1/aif/farm/herds', 'POST', data)}
          onDelete={activeForm.initial?.id ? () => apiDelete(`/api/v1/aif/farm/herds/${activeForm.initial.id}`) : undefined}
        />
      )}
      {activeForm?.type === 'energy' && (
        <EnergyForm
          initial={activeForm.initial}
          onCancel={() => setActiveForm(null)}
          onSave={(data) => activeForm.initial?.id
            ? apiSave(`/api/v1/aif/farm/energy/${activeForm.initial.id}`, 'PATCH', data)
            : apiSave('/api/v1/aif/farm/energy', 'POST', data)}
          onDelete={activeForm.initial?.id ? () => apiDelete(`/api/v1/aif/farm/energy/${activeForm.initial.id}`) : undefined}
        />
      )}
    </AnimatedPage>
  );
}

function AddButton({ label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="mt-2 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-[var(--aif-color-primary)] border border-[var(--aif-color-primary)]/30 hover:bg-[var(--aif-color-primary)]/5 transition w-full justify-center"
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
      {label}
    </button>
  );
}

function UploadButton({ label, context, onUpload }) {
  const fileInputRef = useRef(null);
  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) { onUpload(file, context); e.target.value = ''; }
        }}
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        className="mt-1 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-[var(--aif-text-muted)] border border-dashed border-[var(--aif-border-default)] hover:border-[var(--aif-color-primary)]/40 hover:text-[var(--aif-color-primary)] transition w-full justify-center"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
        {label}
      </button>
    </>
  );
}

function ImportButton({ label, accept, onFile }) {
  const fileInputRef = useRef(null);
  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) { onFile(file); e.target.value = ''; }
        }}
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium text-[var(--aif-text-muted)] border border-dashed border-[var(--aif-border-default)] hover:border-[var(--aif-color-primary)]/40 hover:text-[var(--aif-color-primary)] transition flex-1 justify-center"
      >
        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" /></svg>
        {label}
      </button>
    </>
  );
}

function MetricCell({ value, unit, label, warning }) {
  return (
    <div className="text-center py-2">
      <div className={`text-lg font-bold ${warning ? 'text-red-400' : 'text-[var(--aif-text-primary)]'}`}>
        {value}
        {unit && <span className="text-[10px] font-normal text-[var(--aif-text-muted)] ml-0.5">{unit}</span>}
      </div>
      <div className="text-[10px] text-[var(--aif-text-muted)] leading-tight">{label}</div>
    </div>
  );
}

function Section({ title, expanded, onToggle, children }) {
  return (
    <div className="rounded-2xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] overflow-hidden">
      <button onClick={onToggle} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <span className="text-sm font-medium text-[var(--aif-text-primary)]">{title}</span>
        <svg className={`w-4 h-4 text-[var(--aif-text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

function DataField({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1 text-sm">
      <span className="text-[var(--aif-text-muted)]">{label}</span>
      <span className="text-[var(--aif-text-primary)] font-medium">{value || '—'}</span>
    </div>
  );
}

function Tag({ label, source }) {
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] border border-[var(--aif-border-default)]">
      <SourceBadge source={source} variant="inline" hideReference />
      {label}
    </span>
  );
}
